import React from "react";
import { languagesForAmberUserWebsite } from "locales/langConfig/langConfig";
import styles from "./languageSelectorModal.module.scss";
import { generateCanonicalUrl } from "@Utils/generateHreflangUrls";
import config from "@Config/index";
import wrapperHOC from "@Utils/wrapperHOC";

/**
 * SSR-only Language Selector component for SEO crawlers.
 * Renders a hidden, static list of available languages in the initial HTML
 * so search engines can discover all language variants of the website.
 *
 * This component:
 * - Has no interactivity (no state, no effects, no event handlers)
 * - Is hidden from visual users via CSS (display: none)
 * - Contains semantic HTML with all language options for crawlers
 * - Uses aria-hidden to exclude from accessibility tree
 * - Includes links to language-specific URLs for SEO
 */

interface LanguageSelectorModalSSRProps {
  /** Current user language from Redux (for marking as selected) */
  userLanguage?: string;
  /** Base canonical URL to generate language-specific links */
  canonicalUrl?: string;
  /**
   * When true, the current page has no valid localized variants (e.g. blog/news
   * posts whose translations use different slugs or don't exist). In that case we
   * must not advertise other-language URLs, mirroring the hreflang suppression in
   * MetaConfig. Rendering them would point crawlers at 404 / empty pages.
   */
  excludeAlternateLinks?: boolean;
}

const LanguageSelectorModalSSR: React.FC<LanguageSelectorModalSSRProps> = ({
  userLanguage = "en",
  canonicalUrl = "/",
  excludeAlternateLinks = false,
}) => {
  // Do not emit alternate-language links when the page opts out — the naive
  // slug-prefix links would resolve to 404 (blog) or un-localized shells (news).
  if (excludeAlternateLinks) return null;

  const url = new URL(canonicalUrl, config.WEB_SERVICE_URL).href;

  // Filter out Chinese language variants for .com to avoid cross-domain hreflang references
  // The .cn domain is treated as a separate, independent website
  const filteredLanguages = languagesForAmberUserWebsite.filter(
    (lang) => !["zh", "zhtw", "zhhk"].includes(lang.code),
  );

  const languages = filteredLanguages.map((lang) => ({
    code: lang.code,
    label: lang.nativeLabel,
    href: canonicalUrl ? generateCanonicalUrl(url, lang.code) : undefined,
  }));

  return (
    <div
      id="language-selector-ssr"
      className={styles.ssrContainer}
      style={{ display: "none" }}
      aria-hidden="true"
      data-ssr-language-selector
    >
      <nav aria-label="Language selection">
        <p>Language Preference</p>
        <ul>
          {languages.map((lang) => (
            <li key={lang.code}>
              {lang.href ? (
                <a
                  href={lang.href}
                  data-lang-code={lang.code}
                  data-selected={lang.code === userLanguage ? "true" : "false"}
                >
                  {lang.label}
                </a>
              ) : (
                <span
                  data-lang-code={lang.code}
                  data-selected={lang.code === userLanguage ? "true" : "false"}
                >
                  {lang.label}
                </span>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

export default wrapperHOC(LanguageSelectorModalSSR, {
  componentName: "LanguageSelectorModalSSR-Header",
  showForChina: false,
});
